import React, { useState } from 'react';
import { X, ExternalLink, Edit3, Trash2, PlusCircle, User, Users, Heart, Clock, BookOpen, Star, Calendar, ShoppingCart } from 'lucide-react';
import AvatarOrInitial from '../Family/AvatarOrInitial';
import QuickRatingInterface from './QuickRatingInterface';

const categoryConfig = {
  project: {
    icon: '🛠️',
    gradient: 'bg-gradient-to-br from-teal-500 to-teal-600',
    bgColor: 'bg-teal-50',
    textColor: 'text-teal-600',
    borderColor: 'border-teal-500',
  },
  learning: {
    icon: '🎓', 
    gradient: 'bg-gradient-to-br from-rose-500 to-pink-600',
    bgColor: 'bg-rose-50',
    textColor: 'text-rose-600',
    borderColor: 'border-rose-500',
  },
  experience: {
    icon: '✨',
    gradient: 'bg-gradient-to-br from-amber-400 to-orange-500',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-600',
    borderColor: 'border-amber-500',
  },
  book: {
    icon: '📚',
    gradient: 'bg-gradient-to-br from-emerald-500 to-emerald-600',
    bgColor: 'bg-emerald-50',
    textColor: 'text-emerald-600',
    borderColor: 'border-emerald-500',
  },
  recipe: {
    icon: '🍳',
    gradient: 'bg-gradient-to-br from-red-500 to-red-600',
    bgColor: 'bg-red-50',
    textColor: 'text-red-600',
    borderColor: 'border-red-500',
  },
};

export default function EnhancedRecommendationModal({
  recommendation,
  onClose,
  onEdit,
  onDelete,
  onConfirmDelete,
  onResponse,
  onStartProject,
  onConvert,
  isParent,
  family,
  currentUser
}) {
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [watchHistoryUpdated, setWatchHistoryUpdated] = useState(false);

  if (!recommendation) return null;

  const config = categoryConfig[recommendation.category] || categoryConfig.project;

  const getUserById = (userId) => {
    const allMembers = [
      ...(family?.parents || []),
      ...(family?.kids || [])
    ];
    return allMembers.find(member => member.id === userId);
  };

  const getTargetKidsDetails = (targetKids) => {
    if (!targetKids || !Array.isArray(targetKids) || targetKids.length === 0) {
      return [];
    }
    return targetKids.map(kidId => getUserById(kidId)).filter(Boolean);
  };

  const createdBy = getUserById(recommendation.createdBy || recommendation.created_by);
  const targetKids = getTargetKidsDetails(recommendation.targetKids);

  // Parse steps from various sources
  const getSteps = () => {
    if (recommendation.steps && recommendation.steps.length > 0) {
      return recommendation.steps;
    }
    
    // Try to extract steps from description
    const stepPattern = /(?:^|\n)\s*(?:\d+[.)]\s*|[-•*]\s*)(.+)/gm;
    const matches = [...(recommendation.description || '').matchAll(stepPattern)];
    
    return matches.map((match, index) => ({
      id: `step-${index}`,
      text: match[1].trim(),
      order: index + 1
    }));
  };

  const steps = getSteps();

  const handleResponse = (responseType) => {
    setSelectedResponse(responseType);
    if (onResponse) {
      onResponse(recommendation.id, responseType);
    }
  };

  // Book-specific response options
  const bookResponseOptions = [
    { key: 'reading_list', label: 'Add to Reading List', icon: '📖', color: 'emerald', description: 'Save for future reading' },
    { key: 'reading_challenge', label: 'Add to Reading Challenge', icon: '🎯', color: 'violet', description: 'Count towards your reading goal' },
    { key: 'buy_now', label: 'Buy This Book', icon: '🛒', color: 'azure', description: 'Purchase and start reading' },
    { key: 'library', label: 'Check Library', icon: '🏛️', color: 'violet', description: 'Look for library availability' },
    { key: 'audiobook', label: 'Find Audiobook', icon: '🎧', color: 'orange', description: 'Listen while commuting' },
    { key: 'gift', label: 'Gift This Book', icon: '🎁', color: 'pink', description: 'Great gift for someone' },
    { key: 'pass', label: 'Not Interested', icon: '✋', color: 'gray', description: 'Not appealing right now' }
  ];

  // Project-specific response options
  const projectResponseOptions = [
    { key: 'project', label: 'Full Project', icon: '🚀', color: 'teal', description: 'Complete project planning and execution' },
    { key: 'big3', label: 'Weekly Goal', icon: '📋', color: 'azure', description: 'Add to my regular activities' },
    { key: 'quick', label: 'Quick Start', icon: '⚡', color: 'orange', description: 'Begin with initial research' },
    { key: 'later', label: 'Save for Later', icon: '⏰', color: 'gray', description: 'Interested but timing isn\'t right' },
    { key: 'learn', label: 'Research Phase', icon: '🔍', color: 'violet', description: 'Learn more before committing' },
    { key: 'pass', label: 'Not Interested', icon: '✋', color: 'red', description: 'Not a good fit right now' }
  ];

  const responseOptions = recommendation.category === 'book' ? bookResponseOptions : projectResponseOptions;

  // Render book-specific layout
  if (recommendation.category === 'book') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto shadow-2xl">
          {/* Book Header */}
          <div className="relative bg-gradient-to-br from-amber-50 to-orange-50 p-8">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-emerald-600/5"></div>
            <div className="relative flex items-start justify-between">
              <div className="flex items-start gap-8">
                {/* Book Cover */}
                <div className="flex-shrink-0">
                  {recommendation.image ? (
                    <div className="relative">
                      <img 
                        src={recommendation.image} 
                        alt={recommendation.title}
                        className="w-40 h-60 object-cover rounded-lg shadow-2xl border-4 border-white"
                      />
                      <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-lg">
                        📚
                      </div>
                    </div>
                  ) : (
                    <div className="w-40 h-60 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg shadow-2xl border-4 border-white flex items-center justify-center">
                      <span className="text-6xl text-white opacity-90">📚</span>
                    </div>
                  )}
                </div>

                {/* Book Details */}
                <div className="flex-1 min-w-0">
                  <h2 className="text-4xl font-bold text-stone-900 mb-3 leading-tight">
                    {recommendation.title}
                  </h2>
                  
                  {/* Author */}
                  {recommendation.author && (
                    <p className="text-xl text-stone-700 mb-4 flex items-center gap-2">
                      <User className="w-5 h-5" />
                      by {recommendation.author}
                    </p>
                  )}

                  {/* Quick Book Info */}
                  <div className="flex flex-wrap gap-6 mb-6">
                    {recommendation.source && (
                      <div className="flex items-center gap-2 text-stone-600">
                        <BookOpen className="w-4 h-4" />
                        <span className="text-sm font-medium">{recommendation.source}</span>
                      </div>
                    )}
                    
                    {recommendation.metaTags?.["og:site_name"] && (
                      <div className="flex items-center gap-2 text-stone-600">
                        <ExternalLink className="w-4 h-4" />
                        <span className="text-sm font-medium">Available on {recommendation.metaTags["og:site_name"]}</span>
                      </div>
                    )}
                  </div>

                  {/* Book Tags */}
                  {recommendation.tags && recommendation.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {recommendation.tags.slice(0, 4).map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium"
                        >
                          {typeof tag === 'string' ? tag : tag.text}
                        </span>
                      ))}
                      {recommendation.tags.length > 4 && (
                        <span className="px-3 py-1 bg-stone-100 text-stone-600 rounded-full text-sm font-medium">
                          +{recommendation.tags.length - 4} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              <button
                onClick={onClose}
                className="p-2 text-stone-500 hover:text-stone-700 hover:bg-white hover:bg-opacity-50 rounded-lg transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-8">
            {/* Book Description */}
            <div className="bg-emerald-50 rounded-2xl p-6 mb-8 border-l-4 border-emerald-500">
              <h4 className="font-bold text-lg mb-3 text-emerald-600 flex items-center gap-2">
                📖 Book Summary
              </h4>
              <p className="text-stone-700 leading-relaxed text-lg">
                {recommendation.description}
              </p>
            </div>

            {/* Book Metadata Grid */}
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Recommendation Details */}
              <div className="bg-stone-50 rounded-xl p-6">
                <h4 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  Recommendation Details
                </h4>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <AvatarOrInitial
                      avatar={createdBy?.avatar}
                      name={createdBy?.name}
                      size="md"
                    />
                    <div>
                      <div className="font-semibold text-stone-900">
                        Recommended by {createdBy?.name || 'Unknown User'}
                      </div>
                      <div className="text-sm text-stone-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(recommendation.created_at).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 className="text-sm font-semibold text-stone-700 mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Suggested for
                    </h5>
                    {targetKids.length === 0 ? (
                      <div className="flex items-center gap-2 text-stone-600">
                        <span>Everyone in the family</span>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {targetKids.map(kid => (
                          <div key={kid.id} className="flex items-center gap-2 bg-white rounded-lg px-3 py-1">
                            <AvatarOrInitial
                              avatar={kid?.avatar}
                              name={kid?.name}
                              size="sm"
                            />
                            <span className="font-medium text-stone-900">{kid?.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-gradient-to-br from-emerald-50 to-emerald-50 rounded-xl p-6">
                <h4 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-emerald-600" />
                  Quick Access
                </h4>
                
                <div className="space-y-3">
                  {/* External Links */}
                  {recommendation.links && recommendation.links.length > 0 && (
                    <div className="space-y-2">
                      {recommendation.links.slice(0, 2).map((link, index) => (
                        <a
                          key={index}
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-white hover:bg-emerald-50 rounded-lg transition-colors group border border-emerald-200"
                        >
                          <ExternalLink className="w-4 h-4 text-emerald-600" />
                          <span className="text-emerald-700 group-hover:text-emerald-800 font-medium truncate text-sm">
                            {link.includes('amazon') ? 'View on Amazon' : 
                             link.includes('goodreads') ? 'See on Goodreads' : 
                             'View Book'}
                          </span>
                        </a>
                      ))}
                    </div>
                  )}

                  {/* URL if no specific links */}
                  {(!recommendation.links || recommendation.links.length === 0) && recommendation.url && (
                    <a
                      href={recommendation.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-white hover:bg-emerald-50 rounded-lg transition-colors group border border-emerald-200"
                    >
                      <ExternalLink className="w-4 h-4 text-emerald-600" />
                      <span className="text-emerald-700 group-hover:text-emerald-800 font-medium truncate text-sm">
                        View Original Source
                      </span>
                    </a>
                  )}
                </div>
              </div>
            </div>



            {/* Parent Actions */}
            {isParent && (
              <div className="border-t border-stone-200 pt-8">
                <div className="flex gap-4">
                  <button
                    onClick={() => onEdit(recommendation)}
                    className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-colors"
                  >
                    <Edit3 className="w-5 h-5" />
                    Edit Recommendation
                  </button>
                  <button
                    onClick={() => onDelete(recommendation.id)}
                    className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                    Remove
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Original layout for non-book categories
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto shadow-2xl">
        {/* Hero Header */}
        <div className={`relative ${config.gradient} p-8 text-white`}>
          {/* Feature image background */}
          {recommendation.image && (
            <div className="absolute inset-0">
              <img 
                src={recommendation.image} 
                alt={recommendation.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black bg-opacity-30"></div>
            </div>
          )}
          
          {/* Background decoration (only when no image) */}
          {!recommendation.image && (
            <>
              <div className="absolute -top-8 -right-8 w-32 h-32 bg-white bg-opacity-10 rounded-full"></div>
              <div className="absolute top-4 -left-4 w-16 h-16 bg-white bg-opacity-5 rounded-full"></div>
            </>
          )}
          
          <div className="relative flex items-start justify-between z-10">
            <div className="flex items-center gap-6">
              <div className="text-6xl opacity-90 drop-shadow-lg">
                {config.icon}
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-2 leading-tight">
                  {recommendation.title}
                </h2>
                <p className="text-white text-opacity-90 text-lg">
                  {recommendation.category === 'project' && 'Design + Engineering Project'}
                  {recommendation.category === 'learning' && 'Learning Course'}
                  {recommendation.category === 'experience' && 'Experience Activity'}
                  {recommendation.category === 'book' && 'Book Recommendation'}
                  {recommendation.category === 'recipe' && 'Recipe Guide'}
                  {recommendation.category === 'movie' && (recommendation.isTV ? 'TV Show' : 'Movie')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Rotten Tomatoes button for movies */}
              {recommendation.category === 'movie' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const title = recommendation.title || '';
                    const year = recommendation.release_year || '';
                    const searchQuery = encodeURIComponent(`${title} ${year}`.trim());
                    const rtUrl = `https://www.rottentomatoes.com/search?search=${searchQuery}`;
                    window.open(rtUrl, '_blank');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition-all text-white font-medium"
                  title="View on Rotten Tomatoes"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span className="text-sm">🍅 RT</span>
                </button>
              )}

              <button
                onClick={onClose}
                className="p-2 text-white text-opacity-70 hover:text-opacity-100 hover:bg-white hover:bg-opacity-20 rounded-lg transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-8">
          {/* Overview Section */}
          <div className={`${config.bgColor} rounded-2xl p-6 mb-8 ${config.borderColor} border-l-4`}>
            <h4 className={`font-bold text-lg mb-3 ${config.textColor} flex items-center gap-2`}>
              {recommendation.category === 'recipe' ? '🍳 Recipe Overview' : 
               recommendation.category === 'book' ? '📚 Book Overview' :
               recommendation.category === 'movie' ? '🎬 Movie Overview' : 
               '🎯 Project Overview'}
            </h4>
            <p className="text-stone-600 leading-relaxed text-lg">
              {recommendation.description}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Creator and Target Info */}
            <div className="bg-stone-50 rounded-xl p-6">
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Created by
                  </h4>
                  <div className="flex items-center gap-3">
                    <AvatarOrInitial
                      avatar={createdBy?.avatar}
                      name={createdBy?.name}
                      size="md"
                    />
                    <div>
                      <div className="font-semibold text-stone-900">
                        {createdBy?.name || 'Unknown User'}
                      </div>
                      <div className="text-sm text-stone-500">
                        {new Date(recommendation.created_at).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Suggested for
                  </h4>
                  {targetKids.length === 0 ? (
                    <div className="flex items-center gap-2 text-stone-600">
                      <Users className="w-5 h-5 text-stone-400" />
                      <span>Everyone in the family</span>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {targetKids.map(kid => (
                        <div key={kid.id} className="flex items-center gap-3">
                          <AvatarOrInitial
                            avatar={kid?.avatar}
                            name={kid?.name}
                            size="sm"
                          />
                          <span className="font-medium text-stone-900">{kid?.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tags and Metadata */}
            <div className="space-y-6">
              {/* Tags */}
              {recommendation.tags && recommendation.tags.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-stone-700 mb-3">Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {recommendation.tags.map((tag, index) => (
                      <span
                        key={index}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                          tag.type === 'college' 
                            ? 'bg-violet-100 text-violet-700' 
                            : 'bg-stone-100 text-stone-700'
                        }`}
                      >
                        {typeof tag === 'string' ? tag : tag.text}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Complexity */}
              {recommendation.complexity && (
                <div>
                  <h4 className="text-sm font-semibold text-stone-700 mb-3">Complexity Level</h4>
                  <div className={`inline-flex items-center gap-2 px-4 py-2 ${config.bgColor} rounded-lg`}>
                    <span className={`text-sm font-semibold ${config.textColor}`}>
                      {recommendation.complexity.charAt(0).toUpperCase() + recommendation.complexity.slice(1)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Recipe Ingredients Section */}
          {recommendation.category === 'recipe' && recommendation.materials_needed && recommendation.materials_needed.length > 0 && (
            <div className="mb-8">
              <h4 className="text-xl font-bold text-stone-900 mb-6">🥘 Ingredients</h4>
              <div className="bg-red-50 rounded-2xl p-6 border-l-4 border-red-500">
                <h5 className="font-semibold text-red-600 mb-4">📋 Shopping List</h5>
                <div className="grid md:grid-cols-2 gap-2">
                  {recommendation.materials_needed.map((ingredient, index) => (
                    <div key={index} className="flex items-center gap-3 py-2">
                      <div className="w-2 h-2 bg-red-400 rounded-full flex-shrink-0"></div>
                      <span className="text-stone-700 leading-relaxed">
                        {ingredient}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-white rounded-lg border border-red-200">
                  <p className="text-sm text-red-700 font-medium">
                    💡 Total ingredients: {recommendation.materials_needed.length}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Recipe Instructions Section */}
          {recommendation.category === 'recipe' && steps.length > 0 && (
            <div className="mb-8">
              <h4 className="text-xl font-bold text-stone-900 mb-6">👨‍🍳 Cooking Instructions</h4>
              <div className="bg-red-50 rounded-2xl p-6 border-l-4 border-red-500">
                <h5 className="font-semibold text-red-600 mb-4">🚀 Step-by-Step Guide</h5>
                <ol className="space-y-4 counter-reset">
                  {steps.map((step, index) => (
                    <li key={step.id || index} className="flex items-start gap-4 relative pl-8">
                      <div className="absolute left-0 top-1 w-6 h-6 bg-gradient-to-br from-red-500 to-red-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {step.order || index + 1}
                      </div>
                      <div className="flex-1 pt-0.5">
                        <p className="text-stone-700 leading-relaxed">
                          {step.text}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {/* Regular Steps Section (for non-recipe categories) */}
          {recommendation.category !== 'recipe' && steps.length > 0 && (
            <div className="mb-8">
              <h4 className="text-xl font-bold text-stone-900 mb-6">Getting Started Roadmap</h4>
              <div className={`${config.bgColor} rounded-2xl p-6 ${config.borderColor} border-l-4`}>
                <h5 className={`font-semibold ${config.textColor} mb-4`}>🚀 Ready to Start? Here's Your Roadmap</h5>
                <ol className="space-y-4 counter-reset">
                  {steps.map((step, index) => (
                    <li key={step.id || index} className="flex items-start gap-4 relative pl-8">
                      <div className={`absolute left-0 top-1 w-6 h-6 ${config.gradient} text-white rounded-full flex items-center justify-center text-sm font-bold`}>
                        {step.order || index + 1}
                      </div>
                      <div className="flex-1 pt-0.5">
                        <p className="text-stone-700 leading-relaxed">
                          {step.text}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}

          {/* Links Section */}
          {recommendation.links && recommendation.links.length > 0 && (
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-stone-900 mb-4">Links & Resources</h4>
              <div className="space-y-3">
                {recommendation.links.map((link, index) => (
                  <a
                    key={index}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-azure-50 hover:bg-azure-100 rounded-xl transition-colors group"
                  >
                    <ExternalLink className="w-5 h-5 text-azure-600" />
                    <span className="text-azure-700 group-hover:text-azure-800 font-medium truncate">
                      {link}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Action Section for Kids */}
          {!isParent && (
            <div className="border-t border-stone-200 pt-8">
              {recommendation.category === 'movie' ? (
                // Movie-specific: Quick Rating Interface
                <div className="space-y-6">
                  <div className="text-center">
                    <h4 className="text-xl font-semibold text-stone-900 mb-2">Rate This {recommendation.isTV || recommendation.is_tv ? 'Show' : 'Movie'}</h4>
                    <p className="text-stone-600">Help us recommend better content for you</p>
                  </div>

                  <div className="max-w-md mx-auto">
                    <QuickRatingInterface
                      movie={recommendation}
                      userId={currentUser?.id}
                      currentStatus="unseen"
                      currentRating={null}
                      onUpdate={(history) => {
                        setWatchHistoryUpdated(true);
                        setTimeout(() => onClose(), 1500);
                      }}
                    />
                  </div>
                </div>
              ) : recommendation.category === 'project' ? (
                // Project-specific: Prominent Start Project CTA
                <div className="space-y-6">
                  <div className="text-center">
                    <h4 className="text-xl font-semibold text-stone-900 mb-2">Ready to Start This Project?</h4>
                    <p className="text-stone-600">Set up your personalized project plan and workspace</p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-teal-50 to-teal-100 rounded-2xl p-6 border border-teal-200">
                    <button
                      onClick={() => onStartProject && onStartProject(recommendation)}
                      className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-teal-600 text-white font-semibold text-lg rounded-xl hover:bg-teal-700 transition-colors mb-4"
                    >
                      <span className="text-xl">🚀</span>
                      Start Project Setup
                    </button>
                    
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <button
                        onClick={() => handleResponse('learn')}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-teal-200 rounded-lg text-sm font-medium text-teal-700 hover:bg-teal-50 transition-colors"
                      >
                        <span>🔍</span>
                        Research First
                      </button>
                      <button
                        onClick={() => handleResponse('later')}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-white border border-teal-200 rounded-lg text-sm font-medium text-teal-700 hover:bg-teal-50 transition-colors"
                      >
                        <span>⏰</span>
                        Save Later
                      </button>
                    </div>
                    
                    {/* Add to Future Big3 button for projects */}
                    <button
                      onClick={() => {
                        onResponse && onResponse(recommendation.id, 'add_to_big3');
                        onClose();
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-violet-500 to-violet-600 text-white font-semibold rounded-xl hover:from-violet-600 hover:to-violet-700 transition-colors"
                    >
                      <span>🎯</span>
                      Add to Future Big3
                    </button>
                  </div>
                </div>
              ) : (
                // Non-project: Standard engagement options
                <div className="space-y-6">
                  <h4 className="text-xl font-semibold text-stone-900">How do you want to engage?</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {responseOptions.map(({ key, label, icon, color, description }) => (
                      <button
                        key={key}
                        onClick={() => handleResponse(key)}
                        className={`p-4 rounded-xl border-2 transition-all text-left hover:shadow-md ${
                          selectedResponse === key
                            ? `border-${color}-500 bg-${color}-50`
                            : 'border-stone-200 hover:border-stone-300 bg-white'
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xl">{icon}</span>
                          <span className="font-semibold text-stone-900">{label}</span>
                        </div>
                        <p className="text-sm text-stone-600">{description}</p>
                      </button>
                    ))}
                  </div>
                  
                  {selectedResponse === 'project' && onConvert && (
                    <button
                      onClick={() => onConvert(recommendation)}
                      className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-azure-600 text-white font-semibold text-lg rounded-xl hover:bg-azure-700 transition-colors"
                    >
                      <PlusCircle className="w-5 h-5" />
                      Convert to Activity
                    </button>
                  )}
                  
                  {/* Add to Future Big3 button for non-projects (exclude movies) */}
                  {recommendation.category !== 'movie' && (
                    <button
                      onClick={() => {
                        onResponse && onResponse(recommendation.id, 'add_to_big3');
                        onClose();
                      }}
                      className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-violet-500 to-violet-600 text-white font-semibold text-lg rounded-xl hover:from-violet-600 hover:to-violet-700 transition-colors"
                    >
                      <span className="text-xl">🎯</span>
                      Add to Future Big3
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Parent Actions */}
          {isParent && (
            <div className="border-t border-stone-200 pt-8">
              <div className="flex gap-4">
                <button
                  onClick={() => onEdit(recommendation)}
                  className="flex items-center gap-2 px-6 py-3 bg-azure-600 text-white font-semibold rounded-xl hover:bg-azure-700 transition-colors"
                >
                  <Edit3 className="w-5 h-5" />
                  Edit Recommendation
                </button>
                <button
                  onClick={() => {
                    onClose();
                    onConfirmDelete ? onConfirmDelete(recommendation.id) : onDelete(recommendation);
                  }}
                  className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}